// Реквизиты юридического лица в настройках (тз, раздел 5.5).
//
// ## Зачем экран
//
// Ведомость под подпись охранник подписывает при получении денег, и тз требует в её шапке
// реквизиты юрлица. Колонки лежали в базе с первой миграции, а наружу не отдавались ни одним
// маршрутом: заполнить их было нечем, и напечатанный лист оставался таблицей, а не документом.
//
// ## Почему в настройках, а не в карточке юрлица
//
// Карточки юрлица нет и, скорее всего, не будет: юрлицо у заказчика одно, заводится при
// развёртывании, и заходить в него будут раз в год — поменять подписанта или счёт. Отдельный
// раздел ради одной записи это лишний путь; блок в настройках стоит ровно там, где человек и
// ищет «настройки компании».
//
// ## Почему сохраняется целиком, а не по полю
//
// Реквизиты правят пачкой: пришли новые банковские, сменился директор. Сохранение по каждому
// полю означало бы восемь запросов и восемь записей в журнале об одном событии.

import { useEffect, useState } from 'react';

import { RiBuilding2Line } from '@remixicon/react';

import type { LegalEntityOut, LegalEntityPatch } from '@/shared/api/endpoints/org';
import { useAuthStore } from '@/shared/auth/store';
import {
  useCreateLegalEntityMutation,
  useLegalEntitiesQuery,
  usePatchLegalEntityMutation,
} from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';

const ПРАВО_ПРАВИТЬ = 'core.dictionary.manage';

/**
 * Заведение компании из пустого состояния.
 *
 * До 15.09.2026 пустое состояние было тупиком: оно честно говорило «юрлица нет», а завести
 * его было нечем — ни маршрута, ни кнопки, ни сида в миграции. На чистой базе заказчик
 * упирался в это на первом же шаге настройки.
 *
 * Спрашивается одно название. Остальное — ИНН, ОГРН, банк, подписант — он берёт из устава и
 * найдёт не сразу, а работать надо сегодня; недостающее видно в форме правки и дописывается
 * когда угодно.
 */
function ЗавестиЮрлицо() {
  const [название, задать] = useState('');
  const завести = useCreateLegalEntityMutation();
  return (
    <form
      className='flex w-full max-w-md flex-col gap-3'
      onSubmit={(событие) => {
        событие.preventDefault();
        const имя = название.trim();
        if (!имя) return;
        завести.mutate({ name: имя });
      }}
    >
      <TextField
        id='новое-юрлицо-название'
        label='Название компании'
        hint='Как в уставе: ООО ЧОП «Барк». Печатается в шапке ведомости'
        value={название}
        onChange={задать}
        placeholder='ООО ЧОП «Барк»'
      />
      {завести.error ? <ErrorAlert error={завести.error} /> : null}
      <Button.Root
        type='submit'
        disabled={!название.trim() || завести.isPending}
        className='self-start'
      >
        {завести.isPending ? 'Завожу…' : 'Завести компанию'}
      </Button.Root>
    </form>
  );
}

/**
 * Поля в том порядке, в каком они стоят в шапке документа, и сколько долей занимает каждое.
 *
 * Доли заданы явно, а не «переносом по ширине»: поля тут разной длины — ИНН в десять цифр и
 * банковская строка в семьдесят знаков, — и свободный перенос выстраивает их лесенкой, где
 * подписи соседних полей стоят на разной высоте. Ровно это и читается как «кривовато».
 */
const ПОЛЯ: ReadonlyArray<{
  код: keyof LegalEntityPatch;
  подпись: string;
  пример: string;
  пояснение?: string;
  /** Долей из четырёх на широком экране. */
  доли: 1 | 2 | 3 | 4;
}> = [
  { код: 'name', подпись: 'Название', пример: 'ООО ЧОП «Барк»', доли: 2 },
  { код: 'inn', подпись: 'ИНН', пример: '8602123456', доли: 1 },
  { код: 'kpp', подпись: 'КПП', пример: '860201001', доли: 1 },
  { код: 'ogrn', подпись: 'ОГРН', пример: '1128602012345', доли: 1 },
  {
    код: 'address',
    подпись: 'Юридический адрес',
    пример: '628400, Сургут, улица Энергетиков, 12',
    доли: 3,
  },
  {
    код: 'bank_details',
    подпись: 'Банковские реквизиты',
    пример: 'р/с 40702810100000012345 в ПАО Сбербанк, БИК 044525225',
    пояснение: 'Одной строкой, как в шапке документа',
    доли: 4,
  },
  { код: 'signer_position', подпись: 'Должность подписанта', пример: 'Генеральный директор', доли: 1 },
  { код: 'signer_name', подпись: 'Подписант', пример: 'Шатун А. В.', доли: 1 },
  {
    код: 'signer_basis',
    подпись: 'Действует на основании',
    пример: 'Устава',
    пояснение: 'Печатается только в форме под подпись',
    доли: 2,
  },
];

/** Класс ширины по долям. Собирается таблицей: строка класса в Tailwind обязана быть целой. */
const ШИРИНА: Readonly<Record<1 | 2 | 3 | 4, string>> = {
  1: 'xl:col-span-1',
  2: 'xl:col-span-2',
  3: 'xl:col-span-3',
  4: 'xl:col-span-4',
};

export function РеквизитыЮрлица() {
  const юрлица = useLegalEntitiesQuery();
  const права = useAuthStore((состояние) => состояние.permissions);
  const можно = can(права, ПРАВО_ПРАВИТЬ);

  return (
    <Block
      icon={RiBuilding2Line}
      title='Реквизиты компании'
      note='Печатаются в шапке выгрузки и печатной формы ведомости'
    >
      <ScreenState
        data={юрлица.data}
        error={юрлица.error}
        subject='реквизиты компании'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void юрлица.refetch()}
        loading={<Skeleton className='h-40 w-full' />}
        empty={
          <EmptyState
            icon={RiBuilding2Line}
            title='Компания ещё не заведена'
            description='Без неё не заводится ни один объект и не печатается ни одна ведомость: ведомость принадлежит компании. Достаточно названия, остальное допишете потом.'
            action={можно ? <ЗавестиЮрлицо /> : undefined}
          />
        }
      >
        {(данные) => (
          <div className='flex flex-col gap-6'>
            {данные.items.map((юрлицо) => (
              <Форма key={юрлицо.id} юрлицо={юрлицо} можно={можно} />
            ))}
          </div>
        )}
      </ScreenState>
    </Block>
  );
}

function Форма({ юрлицо, можно }: { юрлицо: LegalEntityOut; можно: boolean }) {
  const сохранить = usePatchLegalEntityMutation();
  const [значения, setЗначения] = useState<Record<string, string>>(() => изЮрлица(юрлицо));
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [сохранено, setСохранено] = useState(false);

  // Данные приехали заново (обновление списка, возврат в раздел) — форма берёт свежее, но
  // только пока человек ничего не менял: затирать набранное на полпути нельзя.
  useEffect(() => {
    if (!сохранить.isPending) setЗначения(изЮрлица(юрлицо));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [юрлицо.id, юрлицо.name, юрлицо.bank_details, юрлицо.signer_name]);

  const изменено = ПОЛЯ.some(({ код }) => значения[код] !== строкой(юрлицо[код as keyof LegalEntityOut]));

  return (
    <form
      className='flex flex-col gap-4'
      noValidate
      onSubmit={(событие) => {
        событие.preventDefault();
        setСохранено(false);
        if (!можно) {
          setЗапрет(
            'Реквизиты меняет администратор: они уходят в подписанный документ и в банковскую шапку.',
          );
          return;
        }
        if ((значения.name ?? '').trim() === '') {
          setЗапрет('Название обязательно: с него начинается шапка каждой ведомости.');
          return;
        }
        setЗапрет(null);
        const тело: LegalEntityPatch = {};
        for (const { код } of ПОЛЯ) {
          const было = строкой(юрлицо[код as keyof LegalEntityOut]);
          const стало = (значения[код] ?? '').trim();
          // Отправляем только изменённое: журнал пишет строку на каждое поле в запросе, и
          // отправив всё, мы получили бы девять записей об одной правке.
          if (стало !== было) тело[код] = стало === '' ? null : стало;
        }
        if (Object.keys(тело).length === 0) {
          setЗапрет('Ничего не изменилось.');
          return;
        }
        сохранить.mutate({ id: юрлицо.id, body: тело }, { onSuccess: () => setСохранено(true) });
      }}
    >
      {/* Сетка, а не перенос по ширине: подписи соседних полей обязаны стоять на одной линии,
          иначе форма из девяти полей читается как лесенка. */}
      <div className='grid grid-cols-1 items-start gap-3 md:grid-cols-2 xl:grid-cols-4'>
        {ПОЛЯ.map(({ код, подпись, пример, пояснение, доли }) => (
          <div key={код} className={ШИРИНА[доли]}>
            <TextField
              id={`legal-${код}`}
              label={подпись}
              value={значения[код] ?? ''}
              onChange={(значение) => {
                setСохранено(false);
                setЗначения((было) => ({ ...было, [код]: значение }));
              }}
              placeholder={пример}
              hint={пояснение}
            />
          </div>
        ))}
      </div>

      {/* Итог словами, а не галочкой: человек правит реквизиты раз в год и хочет убедиться,
          что именно уехало. */}
      {сохранено && !изменено && (
        <Hint.Root>Сохранено. Новые реквизиты попадут в следующую выгрузку ведомости.</Hint.Root>
      )}
      {запрет !== null && <Hint.Root>{запрет}</Hint.Root>}
      {сохранить.isError && <ErrorAlert error={сохранить.error} />}

      <div>
        <Button.Root
          type='submit'
          variant='primary'
          mode='filled'
          size='xsmall'
          disabled={сохранить.isPending}
        >
          {сохранить.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button.Root>
      </div>
    </form>
  );
}

function изЮрлица(юрлицо: LegalEntityOut): Record<string, string> {
  const значения: Record<string, string> = {};
  for (const { код } of ПОЛЯ) значения[код] = строкой(юрлицо[код as keyof LegalEntityOut]);
  return значения;
}

/** Пустое поле показывается пустым, а не словом «null»: это форма ввода, а не отчёт. */
function строкой(значение: unknown): string {
  return значение === null || значение === undefined ? '' : String(значение);
}
